import { useState } from "react";
import DocumentMeta from "@/components/base/DocumentMeta";
import SchemaOrg from "@/components/base/SchemaOrg";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [contactStatus, setContactStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [contactMessage, setContactMessage] = useState("");

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get("phone_alt") as string || "").trim();
    if (honeypotValue) {
      setContactStatus("success");
      setContactMessage("消息已发送！我们会尽快回复你。");
      setContactForm({ name: "", email: "", message: "" });
      return;
    }

    setContactStatus("loading");
    setContactMessage("");

    try {
      const response = await fetch("https://readdy.ai/api/form/d9q54sue3oq4jqumtgj0", {
        method: "POST",
        body: new URLSearchParams({
          name: contactForm.name,
          email: contactForm.email,
          message: contactForm.message,
        }).toString(),
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setContactStatus("success");
        setContactMessage("消息已发送！我们会尽快回复你。");
        setContactForm({ name: "", email: "", message: "" });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || "发送失败，请稍后再试";
        if (serverMsg.includes("spam") || serverMsg.includes("form data is spam")) {
          setContactStatus("error");
          setContactMessage("发送失败，请稍后再试");
        } else {
          setContactStatus("error");
          setContactMessage(serverMsg);
        }
      }
    } catch {
      setContactStatus("error");
      setContactMessage("网络错误，请检查网络后重试");
    }
  };

  const siteUrl = "https://worldcup-tickets-guide.com";
  const aboutUrl = `${siteUrl}/about`;

  const milestones = [
    { year: "2023", title: "平台创立", description: "由一群热爱足球的资深球迷创立，致力于为中文用户提供世界杯门票信息" },
    { year: "2024", title: "内容扩展", description: "覆盖所有世界杯购票渠道，发布超过20篇深度购票指南" },
    { year: "2025", title: "用户增长", description: "累计服务超过10万中国球迷，成为中文世界杯票务资讯首选平台" },
    { year: "2026", title: "全面升级", description: "全面更新2026世界杯购票信息，新增实时更新和社区互动功能" },
  ];

  return (
    <>
      <DocumentMeta
        lang="zh-CN"
        title="关于我们 - 世界杯票务指南"
        description="世界杯票务指南是一家为中国球迷提供世界杯门票购买信息的专业平台，致力于提供最全面、最可靠的世界杯购票渠道与攻略。"
        keywords="世界杯票务指南,关于我们,世界杯门票资讯平台,品牌介绍,联系我们"
        canonical={aboutUrl}
        ogTitle="关于我们 - 世界杯票务指南"
        ogDescription="世界杯票务指南是一家为中国球迷提供世界杯门票购买信息的专业平台。"
        ogType="website"
        ogUrl={aboutUrl}
        ogSiteName="世界杯票务指南"
        ogLocale="zh_CN"
      />

      <SchemaOrg
        type="Organization"
        data={{
          name: "世界杯票务指南",
          url: siteUrl,
          description: "为中国球迷提供世界杯门票购买指南的资讯平台。",
        }}
      />
      <SchemaOrg
        type="WebPage"
        data={{
          name: "关于我们 - 世界杯票务指南",
          url: aboutUrl,
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: siteUrl },
            { "@type": "ListItem", position: 2, name: "关于我们" },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb items={[{ label: "首页", href: "/" }, { label: "关于我们" }]} />

            <div className="mt-6 mb-10">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3">
                关于我们
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl">
                世界杯票务指南 &mdash; 中国球迷身边的世界杯门票购买专家
              </p>
            </div>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">品牌介绍</h2>
              <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4">
                  世界杯票务指南创立于2023年，是一家专注于为中国球迷提供世界杯门票购买信息与指南的专业资讯平台。我们的使命是帮助每一位热爱足球的中国球迷，安全、便捷地购买到心仪的世界杯门票。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base mb-4">
                  我们深知，对于大多数中国球迷来说，购买世界杯门票面临着语言障碍、渠道不明确、真伪难辨等诸多挑战。为此，我们汇集了来自FIFA官网、官方授权代理商等最权威的信息来源，用中文呈现最全面、最及时的购票指南。
                </p>
                <p className="text-foreground-700 leading-relaxed text-sm md:text-base">
                  平台涵盖FIFA官方购票流程、授权代理商对比、球迷套餐选择、抢票技巧与避坑指南等全维度内容，已累计发布30余篇深度购票指南，服务超过10万中国球迷。
                </p>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-px bg-background-200 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((m, idx) => (
                    <div key={idx} className="flex flex-col md:flex-row gap-4 md:gap-8">
                      <div className="flex items-center gap-3 md:w-32 shrink-0">
                        <div className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                          <i className="ri-flag-line text-background-50 text-sm"></i>
                        </div>
                        <span className="text-lg font-bold text-primary-500">{m.year}</span>
                      </div>
                      <div className="md:pl-0 pl-11">
                        <h3 className="text-base font-semibold text-foreground-900 mb-1">{m.title}</h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{m.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">信誉与承诺</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="p-5 bg-background-50 border border-background-200 rounded-lg">
                  <div className="w-11 h-11 flex items-center justify-center bg-accent-100 rounded-lg mb-4">
                    <i className="ri-verified-badge-line text-accent-500 text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">信息来源可靠</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    所有信息均来源于FIFA官网、官方授权代理商等权威渠道。我们坚持不发布未经核实的购票信息。
                  </p>
                </div>
                <div className="p-5 bg-background-50 border border-background-200 rounded-lg">
                  <div className="w-11 h-11 flex items-center justify-center bg-accent-100 rounded-lg mb-4">
                    <i className="ri-price-tag-3-line text-accent-500 text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">非商业盈利平台</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    本站不销售门票，不收取任何中介费用。我们是一个独立的资讯平台，以球迷利益为核心。
                  </p>
                </div>
                <div className="p-5 bg-background-50 border border-background-200 rounded-lg">
                  <div className="w-11 h-11 flex items-center justify-center bg-accent-100 rounded-lg mb-4">
                    <i className="ri-shield-check-line text-accent-500 text-xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">隐私安全保障</h3>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    严格遵守隐私保护法规，用户的个人信息不会被未经授权地收集、使用或向第三方分享。
                  </p>
                </div>
              </div>
            </section>

            <section id="contact" className="mb-14">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-5">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                    有任何关于世界杯门票购买的疑问、建议或合作意向，欢迎通过右侧表单联系我们。我们的团队会尽快回复你。
                  </p>
                  <div className="flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-lg">
                        <i className="ri-mail-line text-foreground-600"></i>
                      </div>
                      <span className="text-sm text-foreground-700">contact@worldcup-tickets-guide.com</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-lg">
                        <i className="ri-wechat-line text-foreground-600"></i>
                      </div>
                      <span className="text-sm text-foreground-700">微信公众号：世界杯票务指南</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-background-100 rounded-lg">
                        <i className="ri-twitter-x-line text-foreground-600"></i>
                      </div>
                      <span className="text-sm text-foreground-700">@WCTicketsGuide</span>
                    </div>
                  </div>
                </div>

                <form
                  onSubmit={handleContactSubmit}
                  className="bg-background-50 border border-background-200 rounded-lg p-6"
                  data-readdy-form=""
                >
                  <div className="mb-4">
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入你的姓名"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      required
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入你的邮箱"
                    />
                  </div>
                  <div className="mb-4">
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="请输入你的留言（500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">{contactForm.message.length}/500</p>
                  </div>

                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="absolute opacity-0 pointer-events-none"
                    style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
                  />

                  <button
                    type="submit"
                    disabled={contactStatus === "loading"}
                    className="w-full py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                  >
                    {contactStatus === "loading" ? "发送中..." : "发送留言"}
                  </button>

                  {contactMessage && (
                    <p
                      className={`mt-3 text-sm text-center ${
                        contactStatus === "success" ? "text-green-600" : "text-red-500"
                      }`}
                    >
                      {contactMessage}
                    </p>
                  )}
                </form>
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}